import {MediaGalleryStateModeEnum, MediaGalleryStateType} from "./MediaGalleryStateType";
import {MediaGalleryMutationsType} from "./MediaGalleryMutationsType";
// eslint-disable-next-line import/named
import Vuex, {ActionTree, GetterTree, Module, MutationTree, Store} from "vuex";
import {MediaGalleryGettersType} from "./MediaGalleryGettersType";
import Vue, {set} from "vue";
import {mediaGalleryService} from "../service/media-gallery-service";
import {MediaGalleryAssetType} from "../types/MediaGalleryAssetType";
import {MediaGalleryFilterType} from "../types/MediaGalleryFilterType";
import {GlobalFilterChangeEvent, GlobalFilterOptionType} from "@global/global-elements/global-filter/GlobalFilterType";
import {MediaGalleryListResponseType} from "../types/MediaGalleryListResponseType";

const state: MediaGalleryStateType = {
    mode: MediaGalleryStateModeEnum.Search,
    authenticated: false,
    acceptedTerms: false,
    runningRequestIdentifiers: [],

    reloadAssetsOnNavigation: true, // Whether the assets need to be reloaded on next navigation. (for search assets only really on first load)
    reloadCollectionAssetsOnNavigation: true, // Whether the collection assets need to be reloaded on next navigation. (for collection assets on first load and after adding new assets to the collection)

    keyword: '',
    assets: [],
    totalAssets: 0,
    filters: [],
    selectedAssetIds: [],

    collectionKeyword: '',
    collectionAssets: [],
    totalCollectionAssets: 0, // filtered amount of items currently matching the collection filters
    collectionFilters: [],
    selectedCollectionAssetIds: [],

    collectedAssetCount: 0 // unfiltered amount of items stred in the session
}

const mutations: MutationTree<MediaGalleryStateType> = {
    setMode(state: MediaGalleryStateType, value: MediaGalleryStateModeEnum) {
        state.mode = value
    },
    setAuthenticated(state: MediaGalleryStateType, value: boolean) {
        state.authenticated = value;
    },
    acceptTerms(state: MediaGalleryStateType, value: boolean) {
        state.acceptedTerms = value
    },
    startRequest(state: MediaGalleryStateType, value: string) {
        state.runningRequestIdentifiers.push(value)
    },
    finishRequest(state: MediaGalleryStateType, value: string) {
        if (state.runningRequestIdentifiers.includes(value)) {
            state.runningRequestIdentifiers.splice(state.runningRequestIdentifiers.indexOf(value), 1)
        }
    },
    setKeyword(state: MediaGalleryStateType, value: string) {
        set(state, state.mode === MediaGalleryStateModeEnum.Collection ? "collectionKeyword" : "keyword", value)
    },
    setAssets(state: MediaGalleryStateType, result: MediaGalleryListResponseType) {
        if (result.start === 0) {
            set(state, state.mode === MediaGalleryStateModeEnum.Collection ? "collectionAssets" : "assets", result.assets)
            set(state, state.mode === MediaGalleryStateModeEnum.Collection ? "totalCollectionAssets" : "totalAssets", result.total)
        } else if (state.mode === MediaGalleryStateModeEnum.Search && result.start === state.assets.length) {
            state.assets = state.assets.concat(result.assets)
        } else if (state.mode === MediaGalleryStateModeEnum.Collection && result.start === state.collectionAssets.length) {
            state.collectionAssets = state.collectionAssets.concat(result.assets)
        } else {
            console.error("Invalid setAsset call", state, "start", result.start, "current assets count", state.mode === MediaGalleryStateModeEnum.Collection ? state.collectionAssets.length : state.assets.length)
        }

        set(state, state.mode === MediaGalleryStateModeEnum.Collection ? "reloadCollectionAssetsOnNavigation" : "reloadAssetsOnNavigation", false)
    },
    setInitialFilters(state: MediaGalleryStateType, filters: MediaGalleryFilterType[]) {
        set(state, 'filters', filters)
        set(state, 'collectionFilters', filters)
    },
    setFilters(state: MediaGalleryStateType, filters: MediaGalleryFilterType[]) {
        if (state.mode === MediaGalleryStateModeEnum.Search) {
            set(state, 'filters', filters)
        }

        if (state.mode === MediaGalleryStateModeEnum.Collection) {
            set(state, 'collectionFilters', filters)
        }
    },
    toggleAssetSelection(state: MediaGalleryStateType, value: string) {
        const selectedIds = state.mode === MediaGalleryStateModeEnum.Collection ? state.selectedCollectionAssetIds : state.selectedAssetIds;

        if (selectedIds.includes(value)) {
            selectedIds.splice(selectedIds.indexOf(value), 1)
        } else {
            selectedIds.push(value)
        }

        set(state, state.mode === MediaGalleryStateModeEnum.Search ? "selectedAssetIds" : "selectedCollectionAssetIds", selectedIds)
    },
    selectAllAssets(state, select: boolean) {
        let assetIds: string[] = []
        if (select) {
            const assets = state.mode === MediaGalleryStateModeEnum.Search ? state.assets : state.collectionAssets;
            assetIds = assets.map(asset => asset.id)
        }

        set(state, state.mode === MediaGalleryStateModeEnum.Search ? "selectedAssetIds" : "selectedCollectionAssetIds", assetIds)
    },
    toggleAssetCollection(state: MediaGalleryStateType, {id, collected}: { id: string, collected: boolean }) {
        // Search-Assets can be updated in-place – If an asset is added/removed that's not already loaded we don't really care.
        state.assets = state.assets.map(asset => {
            if (asset.id === id) {
                asset.collected = collected
            }
            return asset
        });
        // Collection assets are different – removing an asset is fine, but adding an asset to the collection requires a reload of the asset list
        if (!collected) {
            state.collectionAssets = state.collectionAssets.filter(asset => asset.id !== id)
        }
        else {
            state.reloadCollectionAssetsOnNavigation = true
        }
    },
    addToCollection(state, assets: MediaGalleryAssetType[]) {
        const collection = new Set(state.collectionAssets)
        assets.forEach(asset => {
            if (!collection.has(asset)) {
                collection.add(asset)
            }
        })

        set(state, 'collectionAssets', Array.from(collection))
    },
    setCollectedAssetCount(state, value: number) {
        state.collectedAssetCount = value
    }
}

const actions: ActionTree<MediaGalleryStateType, MediaGalleryStoreType> = {
    setMode: ({commit}, payload: MediaGalleryStateModeEnum) => {
        commit('setMode', payload)
    },
    authenticate: ({commit}) => {
        commit('setAuthenticated', !!window.PFL_AUTHENTICATED)
    },
    acceptTerms: ({commit}) => {
        commit('acceptTerms', true)
    },
    setKeyword: ({getters, commit, dispatch}, payload) => {
        if (getters.getKeyword !== payload) {
            commit('setKeyword', payload)
            dispatch('searchAssets');
        }
    },
    async searchAssets({commit, state, dispatch}, loadMore = false) {
        const query = state.mode === MediaGalleryStateModeEnum.Search ? state.keyword : state.collectionKeyword
        const filters = state.mode === MediaGalleryStateModeEnum.Search ? state.filters : state.collectionFilters
        const rows = 12
        const start = loadMore ? (state.mode === MediaGalleryStateModeEnum.Search ? state.assets.length : state.collectionAssets.length) : 0

        // Make sure config is loaded before
        commit('startRequest', 'searchAssets' + state.mode)
        try {
            await dispatch('loadConfig')

            const response = await mediaGalleryService.search(query, filters, rows, start, state.mode)

            let updatedFilters = [...filters]
            updatedFilters = mediaGalleryService.setAvailableOptions(updatedFilters, response.available)
            updatedFilters = mediaGalleryService.setSelectedOptions(updatedFilters, response.selection)

            commit('setFilters', updatedFilters)
            commit('setAssets', response)

            if (response.collectedAssetCount) {
                commit('setCollectedAssetCount', response.collectedAssetCount)
            }
        } catch (e) {
            // Just to prevent message "uncaught error" in console ;-)
        } finally {
            commit('finishRequest', 'searchAssets' + state.mode)
        }
    },
    async loadConfig({state, commit}) {
        // Conffig loads filters. If filters are loaded no config call is needed.
        if (state.filters.length === 0) {
            try {
                commit('startRequest', 'config')
                const response = await mediaGalleryService.config();
                commit('setInitialFilters', mediaGalleryService.convertProductFinderFilterstToMediaGalleyFilters(response.filters))
            } catch (e) {
                // Expected issue if loadConfig is called multiple times
            } finally {
                commit('finishRequest', 'config')
            }
        }
    },
    updateFilter: ({commit, getters, dispatch}, payload: GlobalFilterChangeEvent) => {
        const filters: MediaGalleryFilterType[] = getters.getFilters;
        const updatedFilters: MediaGalleryFilterType[] = [];

        filters.forEach(filter => {
            if (filter.name === payload.filterName) {
                const updatedOptions: GlobalFilterOptionType[] = []
                filter.options.forEach(option => {
                    if (option.key === payload.optionName) {
                        option.selected = !option.selected;
                    }

                    updatedOptions.push(option)
                })

                if (updatedOptions.length > 0) {
                    filter.options = updatedOptions
                }
            }

            updatedFilters.push(filter)
        })

        commit('setFilters', updatedFilters)
        dispatch('searchAssets')
    },
    resetFilters: ({commit, getters, dispatch}) => {
        const filters: MediaGalleryFilterType[] = getters.getFilters;
        const updatedFilters: MediaGalleryFilterType[] = [];

        filters.forEach(filter => {
            const updatedOptions: GlobalFilterOptionType[] = []
            filter.options.forEach(option => {
                option.selected = false;

                updatedOptions.push(option)
            })

            if (updatedOptions.length > 0) {
                filter.options = updatedOptions
            }

            updatedFilters.push(filter)
        })

        commit('setFilters', updatedFilters)
        dispatch('searchAssets')
    },
    toggleAssetSelection({commit}, value) {
        commit('toggleAssetSelection', value)
    },
    selectAllAssets({commit}, select) {
        commit('selectAllAssets', select)
    },
    async toggleAssetCollection({commit}, value: MediaGalleryAssetType | MediaGalleryAssetType[]) {

        if (!Array.isArray(value)) {
            value = [value]
        }

        for (const asset of value) {
            if (asset.collected) {
                try {
                    commit('startRequest', 'collectionRemove' + asset.id)
                    const response = await mediaGalleryService.collectionRemove(asset.id)
                    commit('setCollectedAssetCount', response.collectedAssetCount)
                    commit('toggleAssetCollection', {id: asset.id, collected: false})
                } catch (e) {
                    // If the service call can be rejected we need to catch the error, so that the finally block is executed
                } finally {
                    commit('finishRequest', 'collectionRemove' + asset.id)
                }
            } else {
                try {
                    commit('startRequest', 'collectionAdd' + asset.id)
                    const response = await mediaGalleryService.collectionAdd([asset.id])
                    commit('setCollectedAssetCount', response.collectedAssetCount)
                    commit('toggleAssetCollection', {id: asset.id, collected: true})
                } catch (e) {
                    // If the service call can be rejected we need to catch the error, so that the finally block is executed
                } finally {
                    commit('finishRequest', 'collectionAdd' + asset.id)
                }
            }
        }
    },
    async downloadAsset({commit}, asset: MediaGalleryAssetType) {
        commit('startRequest', 'download')
        await mediaGalleryService.download([asset.id])
        commit('finishRequest', 'download')
    },
    async downloadSelectedAssets({commit, state}) {
        commit('startRequest', 'download')
        const selectedIds = state.mode === MediaGalleryStateModeEnum.Collection ? state.selectedCollectionAssetIds : state.selectedAssetIds;
        await mediaGalleryService.download(selectedIds)
        commit('selectAllAssets', false)
        commit('finishRequest', 'download')
    },
    async addToCollection({commit, getters}) {
        const assetIds = getters.getSelectedAssetIds
        commit('startRequest', 'collection')
        const response = await mediaGalleryService.collectionAdd(assetIds)
        if (response) {
            commit('setCollectedAssetCount', response.collectedAssetCount)
            assetIds.forEach((assetId: string) => {
                commit('toggleAssetCollection', {id: assetId, collected: true})
                commit('selectAllAssets', false)
            })
        }
        commit('finishRequest', 'collection')
    },
    async removeFromCollection({commit, getters, dispatch}) {
        const assetIds = getters.getSelectedAssetIds
        commit('startRequest', 'collection')

        for (const assetId of assetIds) {
            const response = await mediaGalleryService.collectionRemove(assetId)
            if (response) {
                commit('setCollectedAssetCount', response.collectedAssetCount)
                commit('toggleAssetCollection', {id: assetId, collected: false})
            }
        }

        commit('selectAllAssets', false)
        dispatch('searchAssets')
        commit('finishRequest', 'collection')
    }
}

const getters: GetterTree<MediaGalleryStateType, MediaGalleryStoreType> = {
    isAuthenticated: (state) => {
        return state.authenticated
    },
    hasAcceptedTerms: (state) => {
        return state.acceptedTerms
    },
    hasRunningRequests: (state) => {
        return state.runningRequestIdentifiers.length > 0
    },
    hasRunningRequest: (state) => (identifier: string) => {
        return state.runningRequestIdentifiers.includes(identifier)
    },
    getKeyword: (state) => {
        switch (state.mode) {
            case MediaGalleryStateModeEnum.Search:
                return state.keyword
            case MediaGalleryStateModeEnum.Collection:
                return state.collectionKeyword
        }
    },
    getAssets: (state) => {
        switch (state.mode) {
            case MediaGalleryStateModeEnum.Search:
                return state.assets
            case MediaGalleryStateModeEnum.Collection:
                return state.collectionAssets
        }
    },
    getAssetById: (state) => (id: string) => {
        return state.mode === MediaGalleryStateModeEnum.Search
            ? state.assets.find(asset => asset.id === id)
            : state.collectionAssets.find(asset => asset.id === id)
    },
    getTotalAssets: (state): number => {
        switch (state.mode) {
            case MediaGalleryStateModeEnum.Search:
                return state.totalAssets
            case MediaGalleryStateModeEnum.Collection:
                return state.totalCollectionAssets
        }
    },
    getFilters: (state) => {
        switch (state.mode) {
            case MediaGalleryStateModeEnum.Search:
                return state.filters
            case MediaGalleryStateModeEnum.Collection:
                return state.collectionFilters
        }
    },
    getSelectedFilters: (state): MediaGalleryFilterType[] => {
        return state.mode === MediaGalleryStateModeEnum.Search
            ? state.filters.filter(filter => {
                return filter.options.some(option => option.selected)
            })
            : state.collectionFilters.filter(filter => {
                return filter.options.some(option => option.selected)
            })
    },
    getFilterByName: (state) => (filterName: string): MediaGalleryFilterType | null => {
        return state.mode === MediaGalleryStateModeEnum.Search
            ? state.filters.find(filter => filter.name === filterName) || null
            : state.collectionFilters.find(filter => filter.name === filterName) || null
    },
    isAssetSelected: (state) => (id: string) => {
        const selectedIds = state.mode === MediaGalleryStateModeEnum.Collection ? state.selectedCollectionAssetIds : state.selectedAssetIds;
        return selectedIds.includes(id);
    },
    hasSelectedAssets: (state) => {
        const selectedIds = state.mode === MediaGalleryStateModeEnum.Collection ? state.selectedCollectionAssetIds : state.selectedAssetIds;

        return selectedIds.length > 0;
    },
    getSelectedAssetIds: (state) => {
        return state.mode === MediaGalleryStateModeEnum.Collection ? state.selectedCollectionAssetIds : state.selectedAssetIds;
    },
    getCollectedAssetCount: (state) => {
        return state.collectedAssetCount
    }
}

const storeModule: Module<MediaGalleryStateType, MediaGalleryStoreType> = {state, mutations, actions, getters}

Vue.use(Vuex)

export default new Store({
    modules: {
        mediaGallery: storeModule
    }
})

export type MediaGalleryStoreType = {
    state: MediaGalleryStateType,
    mutations: MediaGalleryMutationsType
    getters: MediaGalleryGettersType
}
